import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import memoize from "@RHCommerceDev/utils/memoize";
import { Theme, useMediaQuery } from "@mui/system";
import StytchBulletIcon from "icons/StytchBulletIcon";
import StytchTickIcon from "icons/StytchTickIcon";
import React, { FC } from "react";

export interface PasswordIndicatorProps {
  password?: string;
  strength?: number;
  length?: number;
}

const PasswordIndicator: FC<PasswordIndicatorProps> = ({
  password = "",
  strength = 0
}) => {
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;

  const requirementsMet = [
    password.length >= 8 && password.length <= 20,
    /[A-Z]/.test(password),
    /[a-z].*[a-z]/.test(password),
    /[0-9]/.test(password),
    /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~@]/.test(password)
  ];

  const fulfilledRequirements = requirementsMet.filter(Boolean).length;
  const calculatedStrength =
    (fulfilledRequirements / requirementsMet.length) * 100;

  const desktopMatchesQuery = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("md")
  );

  return (
    <div
      data-testid="password-indicator"
      className={`${desktopMatchesQuery ? "w-[160px]" : "w-full"} h-[160px]`}
    >
      <Typography className="mb-2 font-primary-rhlight !text-[10px] !font-[300] leading-[11px] tracking-[0.44px] text-black">
        {stytchData?.PASSWORD_STRENGTH}
      </Typography>
      <div className="flex items-center">
        <div className="h-[4px] w-[160px] rounded-[10px] bg-gray-300">
          <div
            style={{
              height: "4px",
              width: `${calculatedStrength}%`,
              background: calculatedStrength === 100 ? "#24A148" : "#CA2022",
              borderRadius: "4px"
            }}
          ></div>
        </div>
      </div>
      <div className="mt-4 font-primary-rhlight !text-[10px] !font-[300] leading-[11px] tracking-[0.44px] text-black">
        <Typography className="font-primary-rhlight !text-[10px] !font-[300] leading-[11px] tracking-[0.44px] text-black">
          {stytchData?.Must_include}
        </Typography>
        <ul className="list-none p-0 font-primary-rhlight text-[11px] !font-[300] leading-[13.2px] tracking-[0.44px] text-black">
          <li className="mt-2 flex gap-2">
            {password.length >= 8 && password.length <= 20 ? (
              <StytchTickIcon />
            ) : (
              <StytchBulletIcon />
            )}{" "}
            {stytchData?.characters}
          </li>
          <li className="mt-1 flex gap-2">
            {/[A-Z]/.test(password) ? <StytchTickIcon /> : <StytchBulletIcon />}{" "}
            {stytchData?.upper_case}
          </li>
          <li className="mt-1 flex gap-2">
            {/[a-z].*[a-z]/.test(password) ? (
              <StytchTickIcon />
            ) : (
              <StytchBulletIcon />
            )}{" "}
            {stytchData?.lower_case}
          </li>
          <li className="mt-1 flex gap-2">
            {/[0-9]/.test(password) ? <StytchTickIcon /> : <StytchBulletIcon />}{" "}
            {stytchData?.number}
          </li>
          <li className="mt-1 flex gap-2">
            {/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~@]/.test(password) ? (
              <StytchTickIcon />
            ) : (
              <StytchBulletIcon />
            )}{" "}
            {stytchData?.special_character}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default memoize(PasswordIndicator);
